/**
 * "Install skin?" (W2-09 publish-import; DESIGN.md §5.12 step 2, §7.5; DIALOG.skinInstall): a Phira resource pack
 * that was dropped or opened. Shows the pack (name, author, description, hit effect, own sounds, its nine images),
 * Phira's validation (errors block the install, as Phira would refuse the pack; notes do not), and installs it
 * through the skin store, optionally making it the preview skin right away.
 */

import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { RESPACK_IMAGES, RESPACK_SOUNDS } from '../../formats/respack/types';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { useText } from './hooks';
import { m } from './import.messages';
import s from './Import.module.css';

/** An image from bytes (an object URL set on the element and revoked when it goes). */
export function BlobImage(props: { readonly bytes: Uint8Array; readonly mime: string; readonly alt: string }) {
  const ref = useRef<HTMLImageElement>(null);
  const { bytes, mime } = props;
  useEffect(() => {
    const img = ref.current;
    if (img === null || typeof URL.createObjectURL !== 'function') return undefined;
    const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime }));
    img.src = url;
    return () => {
      img.removeAttribute('src');
      URL.revokeObjectURL(url);
    };
  }, [bytes, mime]);
  return <img ref={ref} alt={props.alt} />;
}

function Messages(props: {
  readonly kind: 'error' | 'warning';
  readonly items: readonly MessageRef[];
  readonly tr: (r: MessageRef) => string;
  readonly label: string;
}) {
  if (props.items.length === 0) return null;
  return (
    <ul className={s.messages} aria-label={props.label}>
      {props.items.map((msg, i) => (
        <li key={`${msg.key}:${String(i)}`} className={s.message} data-kind={props.kind}>
          <Icon name={props.kind === 'error' ? 'OctagonX' : 'TriangleAlert'} size={14} />
          <span>{props.tr(msg)}</span>
        </li>
      ))}
    </ul>
  );
}

/** `DIALOG.skinInstall` (modal). */
export function SkinInstallDialog(props: DialogProps<'skinInstall'>) {
  const { services } = props;
  const { pack, validation } = props.props;
  const text = useText(services);
  const [useNow, setUseNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [installErrors, setInstallErrors] = useState<readonly MessageRef[]>([]);
  const installable = pack !== null && validation.errors.length === 0;
  const close = (r: { readonly installedId: string } | null): void => {
    props.close(r);
  };

  const install = async (): Promise<void> => {
    if (!installable || busy) return;
    setBusy(true);
    try {
      const out = await services.skins.install(pack);
      if (!out.ok) {
        setInstallErrors(out.errors);
        services.ui.toast({ key: m.skinInstallFailed }, { kind: 'error' });
        return;
      }
      if (useNow) {
        await services.skins.setActive(out.id);
        services.settings.set('preview', 'defaultSkin', out.id);
      }
      services.ui.toast({ key: m.skinInstalled, params: { name: pack.info.name } }, { kind: 'success' });
      close({ installedId: out.id });
    } catch (e) {
      setInstallErrors([{ key: m.plainName, params: { name: e instanceof Error ? e.message : String(e) } }]);
      services.ui.toast({ key: m.skinInstallFailed }, { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const sounds = pack === null ? [] : RESPACK_SOUNDS.filter((n) => pack.sounds[n] !== undefined);
  return (
    <Dialog
      open
      size="md"
      title={pack === null ? text.t(m.skinTitleInvalid) : text.t(m.skinTitle, { name: pack.info.name })}
      onClose={() => {
        close(null);
      }}
      footer={
        <div className={s.footer}>
          <Button
            variant="ghost"
            onClick={() => {
              close(null);
            }}
          >
            {text.t(m.cancel)}
          </Button>
          {pack !== null && (
            <Button
              variant="primary"
              icon="Palette"
              disabledReason={installable ? (busy ? { key: m.skinInstall } : null) : (validation.errors[0] ?? null)}
              onClick={() => {
                void install();
              }}
            >
              {text.t(m.skinInstall)}
            </Button>
          )}
        </div>
      }
    >
      <div className={s.dialog}>
        {pack !== null && (
          <div className={s.skinHead}>
            <p className={s.skinName}>{pack.info.name}</p>
            {pack.info.author.trim() !== '' && (
              <span className={s.summaryLine}>{text.t(m.skinBy, { author: pack.info.author })}</span>
            )}
            {pack.info.description.trim() !== '' && <span>{pack.info.description}</span>}
            <span className={s.summaryLine}>
              {text.t(m.skinHitFx, {
                cols: pack.info.hitFx[0],
                rows: pack.info.hitFx[1],
                duration: pack.info.hitFxDuration,
              })}
            </span>
            <span className={s.summaryLine}>
              {sounds.length > 0 ? text.t(m.skinSounds, { list: sounds.join(', ') }) : text.t(m.skinNoSounds)}
            </span>
          </div>
        )}
        <Messages
          kind="error"
          items={[...validation.errors, ...installErrors]}
          tr={text.tr}
          label={text.t(m.skinErrors)}
        />
        <Messages kind="warning" items={validation.warnings} tr={text.tr} label={text.t(m.skinWarnings)} />
        {pack !== null && (
          <ul className={s.images} aria-label={text.t(m.skinImages)}>
            {RESPACK_IMAGES.map((name) => (
              <li key={name} className={s.image}>
                <BlobImage bytes={pack.images[name]} mime="image/png" alt={text.t(m.imageAlt, { name })} />
                <span>{name}</span>
              </li>
            ))}
          </ul>
        )}
        {installable && (
          <Checkbox
            label={text.t(m.skinUseNow)}
            checked={useNow}
            onChange={(v) => {
              setUseNow(v);
            }}
          />
        )}
      </div>
    </Dialog>
  );
}
